import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@/components/layout/AppShell';
import { PageContainer } from '@/components/layout/PageContainer';
import { TopBar } from '@/components/layout/TopBar';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { Banner } from '@/components/ui/Banner';
import { WalletCard } from '@/components/domain/WalletCard';
import { BalanceFormatter } from '@/components/domain/BalanceFormatter';
import { ListItem } from '@/components/ui/ListItem';
import { IcArrowDown, IcArrowUp } from '@/components/ui/icons';
import { useWallet, useWalletTransactions } from '@/api/queries/wallet';
import { useMySubscription, useSubscriptionPlans } from '@/api/queries/subscription';
import { useAutoBookingStatus } from '@/api/queries/user';
import { useMe } from '@/api/queries/user';
import { paths } from '@/routes/paths';
import { centsToUsdString } from '@/utils/money';
import { useLocale } from '@/hooks/useLocale';
import { formatDate } from '@/utils/date';

export function WalletPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { locale } = useLocale();
  const wallet = useWallet();
  const txs = useWalletTransactions(20);
  const sub = useMySubscription();
  const me = useMe();
  const plans = useSubscriptionPlans(true);
  const abs = useAutoBookingStatus();

  const minBalance = centsToUsdString(abs.data?.min_balance_cents ?? 1_000_000, locale);
  const employerProvided = sub.data?.source === 'employer_provided';

  const offer = plans.data?.find((p) => p.code !== sub.data?.plan_code) ?? plans.data?.[0];

  return (
    <AppShell>
      <PageContainer>
        <TopBar back title={t('wallet.title')} bell onBellClick={() => navigate(paths.notifications)} />
        <WalletCard wallet={wallet.data ?? null} loading={wallet.isLoading} showAllAction={false} />

        {/* Subscription block — General Wallet 1/2/3 states */}
        {!sub.data ? (
          <Card variant="light" padding="md">
            <div style={{ fontWeight: 600, marginBottom: 6 }}>
              {t('wallet.subscription_block.free_title')}
            </div>
            <p style={{ margin: 0, color: 'var(--color-text-on-light-muted)' }}>
              {t('wallet.subscription_block.free_body', { minBalance })}
            </p>
            <Button block style={{ marginTop: 12 }} onClick={() => navigate(paths.subscribePlans)}>
              {t('onboarding.subscribe.subscribe')}
            </Button>
          </Card>
        ) : employerProvided ? (
          <Card variant="light" padding="md">
            <div style={{ fontWeight: 600 }}>{t('wallet.subscription_block.employer_title')}</div>
            <p style={{ margin: 6, color: 'var(--color-text-on-light-muted)' }}>
              {t('wallet.subscription_block.employer_body')}
            </p>
            {offer && (
              <>
                <div style={{ fontWeight: 600, marginTop: 12 }}>
                  {t('wallet.subscription_block.upsell_title')}
                </div>
                <p style={{ margin: 6, color: 'var(--color-text-on-light-muted)' }}>
                  {t('wallet.subscription_block.upsell_body', {
                    price: centsToUsdString(offer.amount_cents, locale),
                  })}
                </p>
                <Button
                  block
                  style={{ marginTop: 12 }}
                  onClick={() => navigate(paths.subscribePayment(offer.code))}
                >
                  {t('onboarding.subscribe.subscribe')}
                </Button>
              </>
            )}
          </Card>
        ) : (
          <Card variant="light" padding="md">
            <div style={{ fontWeight: 600 }}>{t('wallet.subscription_block.subscribed_title')}</div>
            <p style={{ margin: 6, color: 'var(--color-text-on-light-muted)' }}>
              {t('wallet.subscription_block.subscribed_body', {
                date: formatDate(sub.data.current_period_end, locale),
              })}
            </p>
            <Button block style={{ marginTop: 12 }} variant="secondary">
              {t('wallet.subscription_block.manage')}
            </Button>
          </Card>
        )}

        {(abs.data?.gap_cents ?? 0) > 0 && (
          <Banner
            variant="warning"
            title={t('home.topup_banner.title', { amount: minBalance })}
            action={
              <Button size="sm" onClick={() => navigate(paths.walletTopup)}>
                {t('home.topup_banner.cta')}
              </Button>
            }
          />
        )}

        <h2 style={{ marginBlock: 8 }}>{t('wallet.transactions')}</h2>
        {txs.isLoading && <Spinner />}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {(txs.data?.items ?? []).map((x) => (
            <ListItem
              key={x.id}
              leading={x.amount_cents >= 0 ? <IcArrowDown /> : <IcArrowUp />}
              title={x.description}
              subtitle={formatDate(x.occurred_at, locale)}
              trailing={
                <span style={{ color: x.amount_cents >= 0 ? 'var(--color-safe)' : 'var(--color-danger)' }}>
                  {x.amount_cents >= 0 ? '+' : '-'}
                  <BalanceFormatter cents={Math.abs(x.amount_cents)} />
                </span>
              }
            />
          ))}
          {!txs.isLoading && !txs.data?.items?.length && (
            <div style={{ color: 'var(--color-text-on-dark-muted)' }}>—</div>
          )}
        </div>
        {/* Avoid unused vars from me */}
        {me.data ? <span hidden>{me.data.id}</span> : null}
      </PageContainer>
    </AppShell>
  );
}
